import React, { useRef, useState } from 'react';
import { createQuotaPresetFromTargetGroup, errorLines } from '../../api';
import { Button, InlineAlert, Modal, TextField, cx, useToast } from '../../ui';
import { useAuth } from '../auth/AuthProvider';

/**
 * Which quota rows the preset will be made from:
 *  - 'page':  the rows on the page are valid, so they are saved to the target group first, then become the preset;
 *  - 'saved': the page has nothing valid to save, but the target group has saved quotas: those are used;
 *  - 'none':  nothing to make a preset from yet.
 */
export type PresetSource = 'page' | 'saved' | 'none';

interface SaveQuotaPresetDialogProps {
  open: boolean;
  targetGroupId: string;
  source: PresetSource;
  /** saves the rows on the page to the target group (without leaving the step); used when source is 'page' */
  savePageQuotas?: () => Promise<void>;
  suggestedName?: string;
  onClose: () => void;
}

/**
 * Save this target group's quota rows as a preset (unit "completes"). Scope: "My client" (the signed-in user's
 * client) or company-wide (clientId null). The server makes the preset from the quotas SAVED on the target group, so
 * when the rows on the page are valid they are saved first (in place, without leaving the step) and then used.
 * A target group with no saved quotas is refused by the server (400).
 */
export function SaveQuotaPresetDialog({ open, targetGroupId, source, savePageQuotas, suggestedName = '', onClose }: SaveQuotaPresetDialogProps) {
  const { user } = useAuth();
  const { notify } = useToast();
  const clientId = user && typeof user.clientId === 'string' && user.clientId ? user.clientId : null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<'client' | 'company'>(clientId ? 'client' : 'company');
  const [nameError, setNameError] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setName(suggestedName); setDescription(''); setScope(clientId ? 'client' : 'company'); setNameError(''); setProblems([]); }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current) return;
    if (!name.trim()) { setNameError('Enter a name for the preset.'); return; }
    setNameError('');
    savingRef.current = true;
    setSaving(true);
    setProblems([]);
    try {
      if (source === 'page' && savePageQuotas) await savePageQuotas(); // the preset is made from what is saved
      await createQuotaPresetFromTargetGroup(targetGroupId, { name: name.trim(), description: description.trim() || undefined, clientId: scope === 'client' ? clientId : null });
      notify('Preset saved', { tone: 'ok' });
      onClose();
    } catch (err) {
      setProblems(errorLines(err));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const option = (value: 'client' | 'company', title: string, hint: string, disabled = false) => (
    <label className={cx('flex items-start gap-3 rounded-lg border px-3.5 py-3 text-sm', disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer', scope === value ? 'border-brand-300 bg-brand-50/50' : 'border-line')}>
      <input type="radio" name="preset-scope" className="mt-0.5 h-4 w-4 accent-brand-600" checked={scope === value} disabled={disabled} onChange={() => setScope(value)} />
      <span><span className="block font-semibold text-ink">{title}</span><span className="block text-xs text-muted">{hint}</span></span>
    </label>
  );

  return (
    <Modal
      open={open}
      size="md"
      title="Save as preset"
      onClose={() => { if (!savingRef.current) onClose(); }}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form="save-preset-form" loading={saving} disabled={source === 'none'}>Save preset</Button>
        </>
      )}
    >
      <form id="save-preset-form" onSubmit={submit} noValidate className="space-y-5">
        {source === 'page' && <p className="text-sm text-muted">The quotas on this page are saved to this target group first, then saved as a preset.</p>}
        {source === 'saved' && <p className="text-sm text-muted">The quotas on this page can't be saved yet (check the highlighted rows), so the quotas already saved for this target group are used.</p>}
        {source === 'none' && <InlineAlert tone="warn">There are no quotas to save yet. Add quota rows on this page first (and fix any highlighted problems), then save them as a preset.</InlineAlert>}
        <TextField label="Preset name" required autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} error={nameError} />
        <TextField label="Description" value={description} maxLength={240} onChange={(e) => setDescription(e.target.value)} hint="Optional. Shown in the list." />
        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-ink">Who can use it</legend>
          <div className="grid gap-2.5">
            {option('client', 'My client', clientId ? 'Only people in your client can apply it' : 'Not available: your login has no client', !clientId)}
            {option('company', 'Company-wide', 'Everyone can apply it')}
          </div>
        </fieldset>
        {problems.length > 0 && (
          <InlineAlert>{problems.length === 1 ? problems[0] : <><b>{problems[0]}</b><ul className="ml-4 mt-1 list-disc">{problems.slice(1).map((m) => <li key={m}>{m}</li>)}</ul></>}</InlineAlert>
        )}
      </form>
    </Modal>
  );
}
